import { useEffect, useState, useCallback } from 'react'
import { StepType, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { categoryDraftToTaskContent } from '../model/categoryDraft'
import { IContentFormData, PageStep } from '../types'
import { IDraftData } from '../draftStore'

export const useCreateTaskV2 = (initialDraft?: IDraftData | null) => {
  // If restoring from a step-1 draft, skip straight to the settings page
  const [pageStep, setPageStep] = useState<PageStep>(() => (initialDraft?.pageStep === 1 ? 1 : 0))
  const [savedContentData, setSavedContentData] = useState<IContentFormData | undefined>(() =>
    initialDraft?.pageStep === 1 ? initialDraft : undefined
  )

  const { updateFormData, setStepStack, sumbitStep, stepStack, goToPreviousStep, isEdit } = useFormStepContext()

  const isTimeFrameStep = stepStack[stepStack.length - 1] === StepType.TIME_FRAME_SELECTION // eslint-disable-line react-hooks/exhaustive-deps

  const goToSettings = useCallback(
    (data: IContentFormData) => {
      setSavedContentData(data)

      updateFormData(StepType.SUBTASK_TASK_CREATION, categoryDraftToTaskContent(data, isEdit))
      setStepStack([StepType.TASK_SETTINGS])
      setPageStep(1)
    },
    [updateFormData, setStepStack, isEdit]
  )

  const goToContent = useCallback(() => {
    setPageStep(0)
    setStepStack([StepType.SUBTASK_TASK_CREATION])
  }, [setStepStack])

  const handleBackOnSettings = useCallback(() => {
    if (isTimeFrameStep) {
      goToPreviousStep()
    } else {
      goToContent()
    }
  }, [isTimeFrameStep, goToPreviousStep, goToContent])

  // When restoring to step 1: push the content data into FormStepContext and set the
  // correct sub-step so the shared settings builder restores immediately.
  useEffect(() => {
    if (initialDraft?.pageStep === 1) {
      updateFormData(StepType.SUBTASK_TASK_CREATION, categoryDraftToTaskContent(initialDraft, isEdit))
      setStepStack([StepType.TASK_SETTINGS])
    }
  }, [initialDraft, setStepStack, updateFormData, isEdit])

  return {
    pageStep,
    savedContentData,
    isTimeFrameStep,
    goToSettings,
    goToContent,
    handleBackOnSettings,
    sumbitStep,
  }
}
